/**
 * Инструменты рисования интерактивной гиперболической карты (UI + построение
 * примитивов).
 *
 * Панель инструментов (Требование 8.1) предоставляет: кисть с ползунком размера
 * (Требование 8.2), набор фигур (круг, эллипс, квадрат, прямоугольник, линия,
 * кривая Безье) с режимом заливки/без заливки (Требования 8.3, 8.4), инструмент
 * заливки замкнутой области (Требование 8.5, через {@link buildFillPrimitiveFromLayer})
 * и палитру цветов, применяемых к последующим действиям (Требование 8.6).
 *
 * Все примитивы строятся в **координатах поверхности** (в канонической системе
 * фундаментальной области) и отображаются под текущим видом и геометрией
 * (Требование 8.7). Ввод пользователя (пиксели холста) переводится в координаты
 * поверхности инверсией вида `V⁻¹` — перевод делегируется
 * {@link DrawingLayer.screenToSurface}, единственному источнику истины для
 * пересчёта экран→поверхность.
 *
 * Архитектура «тонкий DOM, чистое ядро»:
 * - {@link buildPrimitive} — **чистая** функция построения `stroke`/`shape`
 *   примитивов из состояния инструмента и опорных точек поверхности; полностью
 *   тестируема без DOM/WebGL.
 * - {@link DrawingTools} координирует состояние инструмента, машину состояний
 *   указателя (`begin`/`update`/`end`) и тонкую DOM-панель. Взаимодействует со
 *   {@link DrawingLayer} и {@link MapViewController} (перевод координат и уровень
 *   детализации для заливки). DOM-часть безопасно вырождается в no-op вне
 *   браузера.
 *
 * Требования: 8.1, 8.2, 8.3, 8.4, 8.6, 8.7
 */

import type {
  Complex,
  DrawingPrimitive,
  EraseMaskPrimitive,
  ShapeKind,
  ShapePrimitive,
  StrokePrimitive,
} from '@langton/shared';
import type { DrawingLayer } from './drawing-layer.js';
import {
  buildFillPrimitiveFromLayer,
  type FloodFillOptions,
} from './flood-fill.js';
import type { MapViewController, Viewport } from './map-view-controller.js';

/** Активный инструмент рисования. */
export type DrawingTool = 'brush' | 'shape' | 'fill' | 'eraser';

/**
 * Режим заливки для фигур (Требование 8.4):
 * - `'fill'` — только заливка внутренней области;
 * - `'stroke'` — только обводка контура;
 * - `'both'` — заливка и обводка.
 * Для незамкнутых фигур (линия, кривая Безье) заливка не применяется —
 * фактические флаги приводятся к обводке (см. {@link resolveShapeFlags}).
 */
export type FillMode = 'fill' | 'stroke' | 'both';

/** Полный набор поддерживаемых фигур (Требование 8.3). */
export const SHAPE_KINDS: readonly ShapeKind[] = [
  'circle',
  'ellipse',
  'square',
  'rectangle',
  'line',
  'bezier',
] as const;

/** Палитра цветов по умолчанию (Требование 8.6). */
export const DEFAULT_COLOR_PALETTE: readonly string[] = [
  '#000000',
  '#ffffff',
  '#e53935',
  '#fb8c00',
  '#fdd835',
  '#43a047',
  '#1e88e5',
  '#8e24aa',
] as const;

/** Ширина мазка/контура по умолчанию (в гиперболической метрике). */
const DEFAULT_WIDTH = 3;
/** Минимальная ширина мазка/контура. */
const MIN_WIDTH = 1;
/** Максимальная ширина мазка/контура (для ползунка). */
const MAX_WIDTH = 40;

/** Радиус ластика по умолчанию — стартовое значение ползунка (Требование 1.4). */
const DEFAULT_ERASER_RADIUS = 10;
/**
 * Минимальное значение ползунка ластика (Требование 1.4). Значение — не радиус в
 * гиперболической метрике напрямую: слой рисования масштабирует его в малый
 * гиперболический радиус через `ERASER_RADIUS_SCALE` при отрисовке маски
 * (Требования 10.1, 10.2).
 */
const MIN_ERASER_RADIUS = 1;
/** Максимальное значение ползунка ластика (Требование 1.4). */
const MAX_ERASER_RADIUS = 40;

/** Состояние инструмента рисования. */
export interface DrawingToolState {
  /** Активный инструмент. */
  tool: DrawingTool;
  /** Выбранная фигура (актуальна при `tool === 'shape'`). */
  shape: ShapeKind;
  /** Режим заливки фигур (Требование 8.4). */
  fillMode: FillMode;
  /** Ширина мазка/контура (`≥ 1`). */
  width: number;
  /** Текущий цвет (hex), применяемый к последующим действиям (Требование 8.6). */
  color: string;
}

/** Флаги заливки/обводки, вычисленные из режима заливки и типа фигуры. */
export interface ShapeFlags {
  /** Заливать ли внутреннюю область. */
  fill: boolean;
  /** Обводить ли контур. */
  stroke: boolean;
}

/** Замкнута ли фигура (имеет внутреннюю область для заливки). */
export function isClosedShapeKind(shape: ShapeKind): boolean {
  return shape !== 'line' && shape !== 'bezier';
}

/**
 * Вычислить флаги заливки/обводки для фигуры из режима заливки (Требование 8.4).
 * Для незамкнутых фигур (линия/Безье) заливка невозможна — возвращается только
 * обводка.
 *
 * @param shape тип фигуры
 * @param fillMode режим заливки
 */
export function resolveShapeFlags(shape: ShapeKind, fillMode: FillMode): ShapeFlags {
  if (!isClosedShapeKind(shape)) {
    return { fill: false, stroke: true };
  }
  switch (fillMode) {
    case 'fill':
      return { fill: true, stroke: false };
    case 'both':
      return { fill: true, stroke: true };
    case 'stroke':
    default:
      return { fill: false, stroke: true };
  }
}

/** Клонировать комплексную точку. */
function cloneComplex(p: Complex): Complex {
  return { re: p.re, im: p.im };
}

/** Проверить, что точка — корректное комплексное число. */
function isFiniteComplex(p: Complex | undefined | null): p is Complex {
  return (
    !!p &&
    typeof p.re === 'number' &&
    typeof p.im === 'number' &&
    Number.isFinite(p.re) &&
    Number.isFinite(p.im)
  );
}

/**
 * Привести опорные точки эллипса к канонической тройке `[center, axisX, axisY]`,
 * ожидаемой {@link buildEllipsePoints}. Из двух точек перетаскивания
 * `[center, corner]` строится осе-ориентированный эллипс: полуось по вещественной
 * оси задаётся точкой `(corner.re, center.im)`, по мнимой — `(center.re, corner.im)`.
 * Если уже передано `≥ 3` точки — используются как есть.
 */
function ellipseAxisPoints(points: Complex[]): Complex[] {
  if (points.length >= 3) {
    return points.slice(0, 3).map(cloneComplex);
  }
  const [center, corner] = points;
  return [
    cloneComplex(center),
    { re: corner.re, im: center.im },
    { re: center.re, im: corner.im },
  ];
}

/**
 * Построить примитив рисования из состояния инструмента и опорных точек
 * поверхности (**чистая** функция, тестируемая без DOM/WebGL).
 *
 * - `tool === 'brush'` → {@link StrokePrimitive} со всеми точками поверхности,
 *   шириной и цветом (Требование 8.2). Требует `≥ 2` точки.
 * - `tool === 'shape'` → {@link ShapePrimitive} выбранного типа. Опорные точки
 *   интерпретируются {@link DrawingLayer} по типу фигуры: линия/прямоугольник/
 *   квадрат/круг — по двум точкам `[p0, p1]`; эллипс — по тройке
 *   `[center, axisX, axisY]` (достраивается из двух точек, см.
 *   {@link ellipseAxisPoints}); Безье — по всем контрольным точкам. Флаги
 *   заливки/обводки берутся из режима заливки (Требование 8.4). Требует `≥ 2`
 *   точки.
 * - `tool === 'fill'` → `null`: заливка растровая и строится из содержимого слоя
 *   ({@link buildFillPrimitiveFromLayer}), а не из опорных точек.
 *
 * Все точки хранятся в координатах поверхности (Требование 8.7); входные точки
 * клонируются, чтобы результат не разделял ссылок с вызывающей стороной.
 *
 * @param state состояние инструмента (тип, фигура, режим, ширина, цвет)
 * @param points опорные точки в координатах поверхности
 * @returns построенный `stroke`/`shape` примитив либо `null`
 */
export function buildPrimitive(
  state: DrawingToolState,
  points: Complex[],
): StrokePrimitive | ShapePrimitive | null {
  const valid = points.filter(isFiniteComplex).map(cloneComplex);
  const width = normalizeWidth(state.width);
  const color = state.color;

  if (state.tool === 'brush') {
    if (valid.length < 2) {
      return null;
    }
    return { kind: 'stroke', points: valid, width, color };
  }

  if (state.tool === 'shape') {
    if (valid.length < 2) {
      return null;
    }
    const flags = resolveShapeFlags(state.shape, state.fillMode);
    const shapePoints =
      state.shape === 'ellipse' ? ellipseAxisPoints(valid) : valid;
    return {
      kind: 'shape',
      shape: state.shape,
      points: shapePoints,
      fill: flags.fill,
      stroke: flags.stroke,
      color,
      width,
    };
  }

  // Заливка не строится из опорных точек.
  return null;
}

/**
 * Построить примитив-маску стирания {@link EraseMaskPrimitive} из накопленных
 * точек движения ластика и текущего радиуса (**чистая** функция, тестируемая без
 * DOM/WebGL; аналог {@link buildPrimitive}, ветка инструмента «ластик»).
 *
 * Точки хранятся в координатах поверхности как геодезическая ломаная движения
 * ластика — идентична на всех уровнях детализации (Требование 1.6). Нечисловые
 * точки отфильтровываются, остальные клонируются, чтобы результат не разделял
 * ссылок с вызывающей стороной. Радиус (значение ползунка) нормализуется в
 * целочисленный диапазон `[1, 40]` через {@link clampEraserRadius} (Требования
 * 1.4, 1.5); в малый гиперболический радиус диска он масштабируется слоем
 * рисования (`ERASER_RADIUS_SCALE`) при отрисовке маски (Требования 10.1, 10.2).
 *
 * Примитив строится и тогда, когда под маской нет ранее нанесённого контента:
 * при отрисовке ({@link DrawingLayer}) он визуально не меняет слой (Требование
 * 1.8), но остаётся отменяемым шагом (Требование 10). Возвращает `null`, только
 * если после фильтрации не осталось ни одной корректной точки (стирать нечем).
 *
 * @param points точки движения ластика в координатах поверхности
 * @param radius запрошенный радиус области стирания
 * @returns построенная маска стирания либо `null`
 */
export function buildEraseMask(
  points: Complex[],
  radius: number,
): EraseMaskPrimitive | null {
  const valid = points.filter(isFiniteComplex).map(cloneComplex);
  if (valid.length < 1) {
    return null;
  }
  return { kind: 'erase', points: valid, radius: clampEraserRadius(radius) };
}

/** Зажать ширину в допустимый диапазон и привести к конечному числу. */
function normalizeWidth(width: number): number {
  if (!Number.isFinite(width)) {
    return DEFAULT_WIDTH;
  }
  return Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);
}

/**
 * Нормализовать значение ползунка ластика к целому в диапазоне `[1, 40]`
 * (Требования 1.4, 1.5): округление до ближайшего целого и ограничение ближайшей
 * допустимой границей (`≤ 1 → 1`, `≥ 40 → 40`). Само значение — не радиус в
 * гиперболической метрике: слой рисования масштабирует его в малый
 * гиперболический радиус диска через `ERASER_RADIUS_SCALE` (Требования 10.1,
 * 10.2). Нечисловые значения приводятся к стартовому значению
 * {@link DEFAULT_ERASER_RADIUS}.
 *
 * **Чистая** функция — тестируется без DOM/WebGL.
 *
 * @param radius запрошенный размер области стирания
 * @returns целочисленный размер в диапазоне `[1, 40]`
 */
export function clampEraserRadius(radius: number): number {
  if (!Number.isFinite(radius)) {
    return DEFAULT_ERASER_RADIUS;
  }
  const rounded = Math.round(radius);
  return Math.min(Math.max(rounded, MIN_ERASER_RADIUS), MAX_ERASER_RADIUS);
}

/** Опции конфигурации {@link DrawingTools}. */
export interface DrawingToolsOptions {
  /**
   * Слой рисования: источник содержимого для растровой заливки и цель для
   * перевода координат экран→поверхность ({@link DrawingLayer.screenToSurface}).
   */
  layer?: DrawingLayer;
  /**
   * Контроллер вида: предоставляет текущий вид `V` и масштаб для перевода
   * координат и (через масштаб) уровень детализации заливки.
   */
  controller?: MapViewController;
  /** Начальное состояние инструмента (переопределяет значения по умолчанию). */
  initialState?: Partial<DrawingToolState>;
  /** Палитра цветов панели (по умолчанию {@link DEFAULT_COLOR_PALETTE}). */
  colorPalette?: readonly string[];
  /**
   * Колбэк завершения примитива: вызывается, когда `end(...)` построил примитив
   * (кисть/фигура/заливка). Страница (задача 20) добавляет его в слой и
   * перерисовывает карту.
   */
  onPrimitive?: (primitive: DrawingPrimitive) => void;
  /** Колбэк изменения состояния инструмента (для синхронизации внешнего UI). */
  onStateChange?: (state: DrawingToolState) => void;
  /**
   * Провайдер уровня детализации для растровой заливки (Требование 8.5). По
   * умолчанию `0`. Обычно связывается с текущим уровнем LOD карты.
   */
  getFillLevel?: () => number;
  /** Опции flood fill (допуск сравнения цветов и т.п.). */
  floodFillOptions?: FloodFillOptions;
}

/** CSS-классы панели инструментов. */
const TOOLBAR_CLASS = 'drawing-toolbar';
const BUTTON_CLASS = 'drawing-toolbar__button';
const BUTTON_ACTIVE_CLASS = 'drawing-toolbar__button--active';
const GROUP_CLASS = 'drawing-toolbar__group';
const SWATCH_CLASS = 'drawing-toolbar__swatch';
const SWATCH_ACTIVE_CLASS = 'drawing-toolbar__swatch--active';

/** Человекочитаемые подписи фигур для кнопок панели. */
const SHAPE_LABELS: Readonly<Record<ShapeKind, string>> = {
  circle: 'Круг',
  ellipse: 'Эллипс',
  square: 'Квадрат',
  rectangle: 'Прямоугольник',
  line: 'Линия',
  bezier: 'Безье',
};

/** Подписи режимов заливки. */
const FILL_MODE_LABELS: Readonly<Record<FillMode, string>> = {
  fill: 'Заливка',
  stroke: 'Контур',
  both: 'Заливка + контур',
};

/**
 * Инструменты рисования карты: состояние инструмента, машина состояний указателя
 * и панель инструментов.
 *
 * Жизненный цикл:
 * 1. `new DrawingTools({ layer, controller, onPrimitive })`.
 * 2. `setTool`/`setShape`/`setColor`/`setFillMode`/`setWidth` — управление
 *    состоянием (или через панель `attach(container)`).
 * 3. Указатель: `begin(surfacePoint)` → `update(surfacePoint)` (при
 *    перетаскивании) → `end(surfacePoint)` возвращает построенный примитив.
 *    Точки — в координатах поверхности; для перевода из пикселей см.
 *    {@link DrawingTools.screenToSurface}.
 * 4. `dispose()` — снять DOM-панель.
 */
export class DrawingTools {
  private readonly options: DrawingToolsOptions;
  private state: DrawingToolState;
  private readonly palette: readonly string[];

  /** Накопленные опорные точки текущего взаимодействия (координаты поверхности). */
  private points: Complex[] = [];
  /** Активно ли взаимодействие (между `begin` и `end`). */
  private drawing = false;
  /**
   * Радиус области стирания ластика (целое, `1..40`; Требования 1.4, 1.5).
   * Хранится отдельно от {@link DrawingToolState}, так как относится только к
   * инструменту «ластик». Стартовое значение — {@link DEFAULT_ERASER_RADIUS}.
   */
  private eraserRadius = DEFAULT_ERASER_RADIUS;

  // DOM-панель (создаётся в браузере при attach/createToolbar).
  private toolbar: HTMLElement | null = null;
  private toolButtons = new Map<string, HTMLElement>();
  private shapeButtons = new Map<ShapeKind, HTMLElement>();
  private swatches: HTMLElement[] = [];
  private colorInput: HTMLInputElement | null = null;
  private widthInput: HTMLInputElement | null = null;
  private eraserRadiusInput: HTMLInputElement | null = null;
  private fillModeSelect: HTMLSelectElement | null = null;

  constructor(options: DrawingToolsOptions = {}) {
    this.options = options;
    this.palette = options.colorPalette ?? DEFAULT_COLOR_PALETTE;
    this.state = {
      tool: options.initialState?.tool ?? 'brush',
      shape: options.initialState?.shape ?? 'line',
      fillMode: options.initialState?.fillMode ?? 'stroke',
      width: normalizeWidth(options.initialState?.width ?? DEFAULT_WIDTH),
      color: options.initialState?.color ?? this.palette[0] ?? '#000000',
    };
  }

  // -------------------------------------------------------------------------
  // Состояние инструмента (геттеры/сеттеры)
  // -------------------------------------------------------------------------

  /** Копия текущего состояния инструмента. */
  getState(): DrawingToolState {
    return { ...this.state };
  }

  /** Активный инструмент. */
  getTool(): DrawingTool {
    return this.state.tool;
  }

  /** Установить активный инструмент (Требование 8.1). */
  setTool(tool: DrawingTool): void {
    this.state.tool = tool;
    this.cancel();
    this.syncUi();
    this.emitStateChange();
  }

  /** Выбранная фигура. */
  getShape(): ShapeKind {
    return this.state.shape;
  }

  /**
   * Установить тип фигуры и переключить инструмент на «фигуру» (Требование 8.3).
   */
  setShape(shape: ShapeKind): void {
    this.state.shape = shape;
    this.state.tool = 'shape';
    this.cancel();
    this.syncUi();
    this.emitStateChange();
  }

  /** Текущий цвет. */
  getColor(): string {
    return this.state.color;
  }

  /**
   * Установить текущий цвет; он применяется к последующим действиям рисования
   * (Требование 8.6).
   */
  setColor(color: string): void {
    this.state.color = color;
    this.syncUi();
    this.emitStateChange();
  }

  /** Текущий режим заливки. */
  getFillMode(): FillMode {
    return this.state.fillMode;
  }

  /** Установить режим заливки фигур (Требование 8.4). */
  setFillMode(fillMode: FillMode): void {
    this.state.fillMode = fillMode;
    this.syncUi();
    this.emitStateChange();
  }

  /** Текущая ширина мазка/контура. */
  getWidth(): number {
    return this.state.width;
  }

  /** Установить ширину мазка/контура (ползунок кисти, Требование 8.2). */
  setWidth(width: number): void {
    this.state.width = normalizeWidth(width);
    this.syncUi();
    this.emitStateChange();
  }

  /** Текущий радиус области стирания ластика (целое, `1..40`; Требование 1.4). */
  getEraserRadius(): number {
    return this.eraserRadius;
  }

  /**
   * Установить радиус области стирания ластика; значение нормализуется в
   * целочисленный диапазон `[1, 40]` через {@link clampEraserRadius}
   * (Требования 1.4, 1.5).
   */
  setEraserRadius(radius: number): void {
    this.eraserRadius = clampEraserRadius(radius);
    this.syncUi();
    this.emitStateChange();
  }

  // -------------------------------------------------------------------------
  // Машина состояний указателя
  // -------------------------------------------------------------------------

  /** Активно ли рисование в данный момент. */
  isDrawing(): boolean {
    return this.drawing;
  }

  /**
   * Перевести пиксельные координаты холста в координаты поверхности `V⁻¹(w)`,
   * делегируя {@link DrawingLayer.screenToSurface} (Требование 8.7). Требует
   * заданных `layer` и `controller`; иначе возвращает `null`.
   *
   * @param px координата X в пикселях
   * @param py координата Y в пикселях
   * @param viewport размеры холста в пикселях
   */
  screenToSurface(px: number, py: number, viewport: Viewport): Complex | null {
    const { layer, controller } = this.options;
    if (!layer || !controller) {
      return null;
    }
    return layer.screenToSurface(
      px,
      py,
      viewport,
      controller.getViewIsometry(),
      controller.getScale(),
      controller.getGeometry(),
    );
  }

  /**
   * Начать взаимодействие в точке поверхности.
   * - Кисть: начать накопление точек мазка (Требование 8.2).
   * - Фигура: зафиксировать стартовую точку; для фигур по двум точкам вторая
   *   точка инициализируется стартовой и уточняется в {@link update}.
   * - Заливка: запомнить точку клика (примитив строится в {@link end}).
   *
   * @param surfacePoint точка в координатах поверхности
   */
  begin(surfacePoint: Complex): void {
    if (!isFiniteComplex(surfacePoint)) {
      return;
    }
    this.drawing = true;
    const p = cloneComplex(surfacePoint);
    if (this.state.tool === 'shape' && this.state.shape !== 'bezier') {
      // Фигуры по опорным точкам: [start, current] — вторая уточняется движением.
      this.points = [p, cloneComplex(surfacePoint)];
    } else {
      // Кисть / Безье / заливка: накопление точек.
      this.points = [p];
    }
  }

  /**
   * Продолжить взаимодействие в точке поверхности (перетаскивание).
   * - Кисть и Безье: добавить точку в накопитель.
   * - Фигуры по двум точкам: обновить «текущую» точку (второй элемент).
   *
   * @param surfacePoint точка в координатах поверхности
   */
  update(surfacePoint: Complex): void {
    if (!this.drawing || !isFiniteComplex(surfacePoint)) {
      return;
    }
    const p = cloneComplex(surfacePoint);
    if (this.state.tool === 'shape' && this.state.shape !== 'bezier') {
      if (this.points.length < 2) {
        this.points.push(p);
      } else {
        this.points[this.points.length - 1] = p;
      }
    } else {
      this.points.push(p);
    }
  }

  /**
   * Завершить взаимодействие в точке поверхности и построить примитив.
   * - Кисть/фигура: {@link buildPrimitive} по накопленным точкам.
   * - Заливка: {@link buildFillPrimitiveFromLayer} по содержимому слоя от точки
   *   клика (Требование 8.5); требует заданного `layer`.
   * - Ластик: {@link buildEraseMask} по накопленным точкам движения и текущему
   *   радиусу {@link getEraserRadius} (Требования 1.3, 1.6, 1.8).
   *
   * Построенный примитив (если не `null`) передаётся в колбэк `onPrimitive`.
   *
   * @param surfacePoint финальная точка в координатах поверхности (опционально)
   * @returns построенный примитив либо `null`
   */
  end(surfacePoint?: Complex): DrawingPrimitive | null {
    if (!this.drawing) {
      return null;
    }
    if (isFiniteComplex(surfacePoint)) {
      this.update(surfacePoint);
    }
    this.drawing = false;
    const points = this.points;
    this.points = [];

    let primitive: DrawingPrimitive | null = null;
    if (this.state.tool === 'fill') {
      primitive = this.buildFill(points[0]);
    } else if (this.state.tool === 'eraser') {
      // Ластик переиспользует машину состояний кисти (begin/update копят точки);
      // на end собираем маску стирания из точек и текущего радиуса (Требование
      // 1.3, 1.6, 1.8).
      primitive = buildEraseMask(points, this.eraserRadius);
    } else {
      primitive = buildPrimitive(this.state, points);
    }
    if (primitive) {
      this.options.onPrimitive?.(primitive);
    }
    return primitive;
  }

  /** Отменить текущее взаимодействие без построения примитива. */
  cancel(): void {
    this.drawing = false;
    this.points = [];
  }

  /**
   * Построить растровую заливку от точки поверхности через содержимое слоя
   * (Требование 8.5). Возвращает `null`, если слой не задан, seed некорректен или
   * заливка не дала области (например, холст недоступен в не-браузерном окружении).
   */
  private buildFill(seed: Complex | undefined): DrawingPrimitive | null {
    const { layer } = this.options;
    if (!layer || !isFiniteComplex(seed)) {
      return null;
    }
    const level = this.options.getFillLevel?.() ?? 0;
    return buildFillPrimitiveFromLayer(
      layer,
      seed,
      this.state.color,
      level,
      this.options.floodFillOptions,
    );
  }

  // -------------------------------------------------------------------------
  // Панель инструментов (тонкий DOM)
  // -------------------------------------------------------------------------

  /** Корневой DOM-элемент панели (после {@link createToolbar}/{@link attach}). */
  get element(): HTMLElement | null {
    return this.toolbar;
  }

  /**
   * Построить DOM-панель инструментов (Требования 8.1, 1.1): кнопки кисти, фигур,
   * заливки и ластика, переключатель режима заливки, ползунок ширины, ползунок
   * размера ластика и палитра цветов. Выбор в панели связывается с состоянием
   * инструмента. Возвращает `null` вне браузера.
   */
  createToolbar(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }
    if (this.toolbar) {
      return this.toolbar;
    }
    const toolbar = document.createElement('div');
    toolbar.className = TOOLBAR_CLASS;

    toolbar.appendChild(this.buildToolGroup());
    toolbar.appendChild(this.buildShapeGroup());
    toolbar.appendChild(this.buildFillModeGroup());
    toolbar.appendChild(this.buildWidthGroup());
    toolbar.appendChild(this.buildEraserGroup());
    toolbar.appendChild(this.buildColorGroup());

    this.toolbar = toolbar;
    this.syncUi();
    return toolbar;
  }

  /**
   * Построить панель и добавить её в контейнер (Требование 8.1). Безопасно
   * игнорируется вне браузера.
   *
   * @param container контейнер панели
   */
  attach(container: HTMLElement): HTMLElement | null {
    const toolbar = this.createToolbar();
    if (toolbar && container && typeof container.appendChild === 'function') {
      container.appendChild(toolbar);
    }
    return toolbar;
  }

  /** Снять DOM-панель и очистить ссылки на элементы. */
  dispose(): void {
    this.toolbar?.remove();
    this.toolbar = null;
    this.toolButtons.clear();
    this.shapeButtons.clear();
    this.swatches = [];
    this.colorInput = null;
    this.widthInput = null;
    this.eraserRadiusInput = null;
    this.fillModeSelect = null;
    this.cancel();
  }

  /** Группа кнопок инструментов: кисть, заливка, ластик (Требование 1.1). */
  private buildToolGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;

    const brush = this.makeButton('Кисть', () => this.setTool('brush'));
    this.toolButtons.set('brush', brush);
    group.appendChild(brush);

    const fill = this.makeButton('Заливка', () => this.setTool('fill'));
    this.toolButtons.set('fill', fill);
    group.appendChild(fill);

    // Ластик рядом с кистью и заливкой (Требование 1.1). setTool('eraser')
    // делает ластик единственным активным инструментом (Требование 1.2).
    const eraser = this.makeButton('Ластик', () => this.setTool('eraser'));
    this.toolButtons.set('eraser', eraser);
    group.appendChild(eraser);

    return group;
  }

  /** Группа кнопок фигур (Требование 8.3). */
  private buildShapeGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;
    for (const shape of SHAPE_KINDS) {
      const button = this.makeButton(SHAPE_LABELS[shape], () =>
        this.setShape(shape),
      );
      this.shapeButtons.set(shape, button);
      group.appendChild(button);
    }
    return group;
  }

  /** Переключатель режима заливки фигур (Требование 8.4). */
  private buildFillModeGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;

    const label = document.createElement('label');
    label.textContent = 'Режим:';

    const select = document.createElement('select');
    for (const mode of ['stroke', 'fill', 'both'] as FillMode[]) {
      const option = document.createElement('option');
      option.value = mode;
      option.textContent = FILL_MODE_LABELS[mode];
      select.appendChild(option);
    }
    select.value = this.state.fillMode;
    select.addEventListener('change', () => {
      this.setFillMode(select.value as FillMode);
    });

    label.appendChild(select);
    group.appendChild(label);
    this.fillModeSelect = select;
    return group;
  }

  /** Ползунок размера кисти/контура (Требование 8.2). */
  private buildWidthGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;

    const label = document.createElement('label');
    label.textContent = 'Размер:';

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(MIN_WIDTH);
    input.max = String(MAX_WIDTH);
    input.step = '1';
    input.value = String(this.state.width);
    input.addEventListener('input', () => {
      this.setWidth(Number(input.value));
    });

    label.appendChild(input);
    group.appendChild(label);
    this.widthInput = input;
    return group;
  }

  /**
   * Ползунок размера области стирания ластика (Требования 1.4, 1.5): целочисленный
   * диапазон `1..40`, шаг `1`, стартовое значение `10`. Значение нормализуется
   * через {@link clampEraserRadius}.
   */
  private buildEraserGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;

    const label = document.createElement('label');
    label.textContent = 'Ластик:';

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(MIN_ERASER_RADIUS);
    input.max = String(MAX_ERASER_RADIUS);
    input.step = '1';
    input.value = String(this.eraserRadius);
    input.addEventListener('input', () => {
      this.setEraserRadius(Number(input.value));
    });

    label.appendChild(input);
    group.appendChild(label);
    this.eraserRadiusInput = input;
    return group;
  }

  /** Палитра цветов и выбор произвольного цвета (Требование 8.6). */
  private buildColorGroup(): HTMLElement {
    const group = document.createElement('div');
    group.className = GROUP_CLASS;

    for (const color of this.palette) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = SWATCH_CLASS;
      swatch.style.backgroundColor = color;
      swatch.title = color;
      swatch.dataset.color = color;
      swatch.addEventListener('click', () => this.setColor(color));
      this.swatches.push(swatch);
      group.appendChild(swatch);
    }

    const input = document.createElement('input');
    input.type = 'color';
    input.value = normalizeHexForInput(this.state.color);
    input.addEventListener('input', () => this.setColor(input.value));
    group.appendChild(input);
    this.colorInput = input;

    return group;
  }

  /** Создать кнопку панели с подписью и обработчиком клика. */
  private makeButton(label: string, onClick: () => void): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  /** Отразить текущее состояние в элементах панели (активные кнопки и т.п.). */
  private syncUi(): void {
    if (!this.toolbar) {
      return;
    }
    // Активная кнопка инструмента.
    for (const [tool, button] of this.toolButtons) {
      button.classList.toggle(BUTTON_ACTIVE_CLASS, this.state.tool === tool);
    }
    // Активная фигура (подсвечивается только когда выбран инструмент фигуры).
    for (const [shape, button] of this.shapeButtons) {
      const active = this.state.tool === 'shape' && this.state.shape === shape;
      button.classList.toggle(BUTTON_ACTIVE_CLASS, active);
    }
    // Палитра.
    for (const swatch of this.swatches) {
      swatch.classList.toggle(
        SWATCH_ACTIVE_CLASS,
        swatch.dataset.color === this.state.color,
      );
    }
    if (this.colorInput) {
      this.colorInput.value = normalizeHexForInput(this.state.color);
    }
    if (this.widthInput) {
      this.widthInput.value = String(this.state.width);
    }
    if (this.eraserRadiusInput) {
      this.eraserRadiusInput.value = String(this.eraserRadius);
    }
    if (this.fillModeSelect) {
      this.fillModeSelect.value = this.state.fillMode;
    }
  }

  /** Уведомить об изменении состояния инструмента. */
  private emitStateChange(): void {
    this.options.onStateChange?.(this.getState());
  }
}

/**
 * Привести цвет к 6-значному hex для `<input type="color">` (принимает только
 * `#rrggbb`). Некорректные значения приводятся к чёрному.
 */
function normalizeHexForInput(color: string): string {
  if (typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)) {
    return color.toLowerCase();
  }
  if (typeof color === 'string' && /^#[0-9a-fA-F]{3}$/.test(color)) {
    const r = color[1];
    const g = color[2];
    const b = color[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#000000';
}
